/**
 * @dramadetector/ui — json-render module barrel export
 *
 * Usage:
 *   import { ddCatalog, ddRegistry, DDRenderer } from '@dramadetector/ui/json-render';
 *   import type { DDRuntimeState, PatternSpotlightProps } from '@dramadetector/ui/json-render';
 *
 * Architecture: Winston (Discourse #3543/2)
 * Task: #783.S1 — [NocoDB #784]
 * Agent: Diego <diego@liftping.net>
 */

// Catalog + Registry
export {
	ddCatalog,
	ddRegistry,
	DDRenderer,
	parseProps,
	COMPONENT_DEFAULTS,
} from "./catalog";

// Primitive schemas
export {
	DramaCategorySchema,
	SubscriptionTierSchema,
	IssueSeveritySchema,
} from "./schema";

// Runtime state
export { DDRuntimeStateSchema } from "./schema";
export type { DDRuntimeState } from "./schema";

// Component prop schemas
export {
	AnalysisCardPropsSchema,
	DramaScorePropsSchema,
	DramaCategoryBadgePropsSchema,
	NarrativeBlockPropsSchema,
	TierBadgePropsSchema,
	InsightRowPropsSchema,
	SectionDividerPropsSchema,
	ActionButtonPropsSchema,
	PatternSpotlightPropsSchema,
	TransformationInsightPropsSchema,
	StyleConsistencyInsightPropsSchema,
	CommunicationPatternInsightPropsSchema,
	ModuleProgressPropsSchema,
	ComponentPropSchemas,
	ComputedExpressionSchema,
	StringOrComputedSchema,
	DDComputedFunctionNames,
} from "./schema";

export type {
	ComputedExpression,
	DDComputedFunctionName,
} from "./schema";

// Component prop types
export type {
	AnalysisCardProps,
	DramaScoreProps,
	DramaCategoryBadgeProps,
	NarrativeBlockProps,
	TierBadgeProps,
	InsightRowProps,
	SectionDividerProps,
	ActionButtonProps,
	PatternSpotlightProps,
	TransformationInsightProps,
	StyleConsistencyInsightProps,
	CommunicationPatternInsightProps,
	ModuleProgressProps,
} from "./schema";

// Action param schemas
export {
	UpgradeTierParamsSchema,
	ShareResultParamsSchema,
	SaveAnalysisParamsSchema,
	DismissInsightParamsSchema,
	ActionParamSchemas,
} from "./schema";

// Core types used downstream
export type {
	Spec,
	StateModel,
	Catalog,
	VisibilityCondition,
} from "@json-render/core";

export type { ComponentRegistry } from "@json-render/react-native";

// Computed functions for $computed expressions (#1158.P1.5)
export {
	ddComputedFunctions,
	formatScore,
	formatCurrency,
	tierCopy,
	pluralize,
} from "./computed-functions";

// Tier guards (S2 #785)
export {
	injectTierGuards,
	patternSpotlightGuard,
	transformationInsightGuard,
	PREMIUM_COMPONENT_NAMES,
} from "./tier-guards";

// useUISpec hook + fallback spec builder (S3 #786) + streaming (P0.4 #1158)
export {
	useUISpec,
	buildFallbackSpec,
	EDGE_FN_TIMEOUT_MS,
	STREAMING_TIMEOUT_MS,
	STREAMING_FIRST_CHUNK_TIMEOUT_MS,
} from "./use-ui-spec";
export type {
	UseUISpecResult,
	UseUISpecOptions,
	UISpecTier,
} from "./use-ui-spec";

// JSONUIProvider re-export for apps/mobile consumers (avoids direct @json-render/react-native dep)
export { JSONUIProvider } from "@json-render/react-native";
export type { JSONUIProviderProps } from "@json-render/react-native";

// DD_COMPONENT_CATALOG + AnalysisResultView (PoC #783)
export {
	DD_COMPONENT_CATALOG,
	DD_CATALOG_COMPONENT_NAMES,
	DD_CORE_COMPONENT_NAMES,
	DD_PREMIUM_COMPONENT_NAMES,
} from "./component-catalog";
export type {
	CatalogComponentEntry,
	CatalogPropDef,
} from "./component-catalog";

export {
	AnalysisResultView,
	buildPoCSpec,
	USE_JSON_RENDER_POC,
} from "./AnalysisResultView";
export type {
	AnalysisResultInput,
	AnalysisResultViewProps,
} from "./AnalysisResultView";

// SDUI Config system (S2 #1158)
export { SDUI_DEFAULTS } from "./sdui-defaults";
export type {
	SDUIBlurConfig,
	SDUIScoreDeltaConfig,
	SDUIConsentModalConfig,
	SDUIReferralShareConfig,
	SDUISubscriptionPageConfig,
	SDUIOGImageConfig,
	SDUIShareModalFormatId,
	SDUIShareModalFormatOption,
	SDUIShareModalConfig,
	SDUIHabitEngineConfig,
	SDUIDramaCardsConfig,
	SDUISurfaceConfig,
	SDUISurfaceMap,
} from "./sdui-defaults";
export { useSDUIConfig } from "./use-sdui-config";
export { toUISpecTier } from "./tier-utils";

// Prompt generation utilities (for build-time system-prompt.json generation)
export {
	formatComponentListFromCatalog,
	extractComponentNames,
} from "./format-component-list";

// Image registry — satori-compatible component renderers for OG images (#1158.P1.4)
export { ddImageComponents } from "./image-registry";
export { CATEGORY_COLORS as OG_CATEGORY_COLORS } from "./image-registry";

// OG image spec builder (#1158.P1.4)
export { buildOGImageSpec } from "./og-image-spec";
export type { OGImageData } from "./og-image-spec";

// Art-direction spec builders (Sprint-12 Wave 3)
export {
	buildEditorialDarkSpec,
	buildCleanAnalyticSpec,
	buildAlertHighContrastSpec,
	buildGradientDramaSpec,
	buildSpecForArtDirection,
	SPEC_BUILDERS,
} from "./spec-builders";
export type { SpecBuilderConfig } from "./spec-builders";

// ThumbnailSpecV1 — Formalized SDUI contract for image pipeline (Sprint-12 Wave 2)
export {
	thumbnailSpecV1Schema,
	parseThumbnailSpec,
	safeParseThumbnailSpec,
	toRenderSpec,
	getSpecDimensions,
	deriveSpecCacheKey,
	FORMAT_DIMENSIONS,
	ART_DIRECTIONS,
	EMPHASIS_MODES,
	LAYOUT_FORMATS,
} from "./thumbnail-spec-v1";
export type {
	ThumbnailSpecV1,
	SpecElement as ThumbnailSpecElement,
	ArtDirection,
	EmphasisMode,
	LayoutFormat,
} from "./thumbnail-spec-v1";
